import { SW_SKIP_WAITING } from './sw-messages';

/**
 * Watches the service worker for a newer build sitting in "waiting".
 *
 * public/sw.js deliberately never calls skipWaiting() on its own, so a deploy
 * can never swap the bundle under a student mid-exam. This store is the ONLY
 * path to activation: `applyServiceWorkerUpdate` posts SKIP_WAITING to the
 * waiting worker on an explicit tap, and the page reloads on the resulting
 * `controllerchange` — but only the tab that asked. Another tab's reload must
 * not reload this one, which may be a timed sitting.
 */

/** A long-lived standalone app should still learn about a deploy — but not on every foregrounding. */
export const UPDATE_CHECK_MIN_MS = 60 * 60 * 1000;

interface StartOptions {
  reload?: () => void;
  now?: () => number;
}

let started = false;
let registration: ServiceWorkerRegistration | null = null;
let waiting: ServiceWorker | null = null;
let applying = false;
let lastCheck = 0;
let reload: () => void = () => {
  window.location.reload();
};
let now: () => number = () => Date.now();
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function setWaiting(worker: ServiceWorker | null): void {
  if (waiting === worker) return;
  waiting = worker;
  notify();
}

function watchInstalling(reg: ServiceWorkerRegistration, container: ServiceWorkerContainer): void {
  const installing = reg.installing;
  if (!installing) return;
  installing.addEventListener('statechange', () => {
    // "installed" with a controller already in charge = an UPDATE waiting.
    // Without a controller it is the very first install, which needs no toast.
    if (installing.state === 'installed' && container.controller) setWaiting(installing);
  });
}

export function startWatchingServiceWorker(options: StartOptions = {}): void {
  if (started || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  started = true;
  if (options.reload) reload = options.reload;
  if (options.now) now = options.now;

  const container = navigator.serviceWorker;
  container.addEventListener('controllerchange', () => {
    if (applying) reload();
  });
  void container.ready.then((reg) => {
    registration = reg;
    if (reg.waiting && container.controller) setWaiting(reg.waiting);
    reg.addEventListener('updatefound', () => {
      watchInstalling(reg, container);
    });
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkForServiceWorkerUpdate();
  });
}

/** Ask the browser to look for a newer worker, at most once an hour. */
export function checkForServiceWorkerUpdate(): void {
  if (registration === null) return;
  const at = now();
  if (at - lastCheck < UPDATE_CHECK_MIN_MS) return;
  lastCheck = at;
  void registration.update().catch(() => undefined);
}

/** The explicit tap: activate the waiting worker, then reload THIS tab. */
export function applyServiceWorkerUpdate(): void {
  if (waiting === null) return;
  applying = true;
  waiting.postMessage({ type: SW_SKIP_WAITING });
}

export function subscribeServiceWorkerUpdate(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

export function isServiceWorkerUpdateReady(): boolean {
  return waiting !== null;
}
